import { confirmOrderSuccess, triggerConfirmOrder } from '../../../actions/digitalSavings/payment';
import {
    apiTransitionPaystack,
    apiTransitionPaystackStatus,
    checkPaymentsStatus,
    checkPaystackStatusSuccess,
    initPayments,
    submitPaymentSuccess,
    submitPaystackPayments,
} from '../../../actions/digitalSavings/paystack';
import { setExpiredAuth } from '../../../actions/digitalSavings/quote';
import {
    getAccessToken,
    getNetworkProvider,
    getPaystackData,
    getPaystackIsApiPending,
    getPaystackStatusIsApiPending,
    getPaystackStatusIsApiStatus,
    getPhoneNumber,
} from '../../../selectors/digitalSavings/payment';
import {
    getMobileNumberValue,
    getPersonalDetailsId,
    getQuoteId,
} from '../../../selectors/digitalSavings/personalDetails';
import { getPremiumCalculation, getQuoteForm } from '../../../selectors/digitalSavings/quote';
import { getPaymentChannel } from '../../../selectors/digitalSavings/payment';
import axios from 'axios';

const clearIntervalId = (intervalId) => {
    clearInterval(intervalId);
};

let intervalId = null;

// Export function to clear interval from UI components
export const clearPaystackPollingInterval = () => {
    if (intervalId) {
        clearIntervalId(intervalId);
        intervalId = null;
    }
};

// ---------------------------- INITIATE PAYMENT --------------------
export const paystackPaymentService = (store, next, action) => {
    const result = next(action);
    const state = store.getState();
    (async () => {
        if (!getPaystackIsApiPending(state)) {
            store.dispatch(apiTransitionPaystack.pending());
            try {
                const resp = await initiatePaystackPayments(state);
                store.dispatch(submitPaymentSuccess(resp.data));
                store.dispatch(apiTransitionPaystack.success());

                const paymentChannel = getPaymentChannel(state);
                if (paymentChannel === 'Pesa link') {
                    // For PesaLink, we only fetch payment details during initiation
                    // Status checks happen manually when user clicks "Check payment status"
                    return;
                }

                // Start polling for Card and Airtel money payments only
                if (paymentChannel === 'Card' || paymentChannel === 'Airtel money') {
                    intervalId = setInterval(() => {
                        const currentState = store.getState();
                        if (!getPaystackData(currentState)) {
                            clearIntervalId(intervalId);
                            intervalId = null;
                            return;
                        }
                        // Only dispatch if we have payment data
                        if (getPaystackData(currentState)?.response?.data) {
                            store.dispatch(checkPaymentsStatus());
                        }
                    }, 5000); // Check every 5 seconds

                    setTimeout(() => {
                        if (intervalId) {
                            clearIntervalId(intervalId);
                            intervalId = null;
                            const finalState = store.getState();
                            if (getPaystackStatusIsApiStatus(finalState) !== 'success') {
                                store.dispatch(apiTransitionPaystackStatus.failure());
                            }
                        }
                    }, 420000);
                }
            } catch (e) {
                store.dispatch(apiTransitionPaystack.failure());
                //store.dispatch(triggerPaymentsStkPushFailure(e.response));
                clearIntervalId(intervalId);
                return e;
            }
        }
    })();
    return result;
};

const initiatePayload = (state) => {
    const quoteId = getQuoteId(state);
    const quotation = getQuoteForm(state);
    const premium = getPremiumCalculation(state);
    const paymentChannel = getPaymentChannel(state);

    const mobileNumberFromPersonalDetails = getMobileNumberValue(state);
    const phoneNumberFromPayment = getPhoneNumber(state);
    const phoneNumber =
        paymentChannel === 'Card' ? mobileNumberFromPersonalDetails.value : phoneNumberFromPayment.value;

    let paymentChannelString = '';
    if (paymentChannel === 'Card') {
        paymentChannelString = 'card';
    } else if (paymentChannel === 'Airtel money') {
        paymentChannelString = 'airtel';
    } else if (paymentChannel === 'Pesa link') {
        paymentChannelString = 'pesalink';
    }

    const amount =
        quotation.saving_category.value === 'monthly'
            ? premium?.premium?.monthlyContribution
            : quotation.saving_category.value === 'once-off'
            ? premium?.premium?.oneOffContributionAmount
            : premium?.premium?.monthlyContribution + premium?.premium?.oneOffContributionAmount;

    const payload = {
        quote_id: quoteId,
        amount: amount,
        product_category: 'SAVINGS',
        product_code: 'DIGITALSAVINGS',
        payment_channel: paymentChannelString,
        phone_number: phoneNumber,
    };

    return {
        ...payload,
    };
};

const initiatePaystackPayments = async (state) => {
    const loginData = getAccessToken(state);
    try {
        const url = '/om-api/digital-savings/paystack-initiate';
        return await axios.post(url, initiatePayload(state), {
            headers: {
                'Content-Type': 'application/json',
                accessToken: loginData.accessToken,
                // Cookie: 'devEnvironment=ea-qa',
            },
        });
    } catch (e) {
        if (e?.response?.data?.status === 401 || e?.response?.data?.status === 403) {
            store.dispatch(setExpiredAuth(true));
        }
        throw e;
    }
};
// ---------------------------- END -------------------

// ---------------------------- CHECK STATUS --------------------
export const paystackStkStatusService = (store, next, action) => {
    const result = next(action);
    const state = store.getState();
    (async () => {
        // Early return if already successful
        if (getPaystackStatusIsApiStatus(state) === 'success') {
            clearIntervalId(intervalId);
            intervalId = null;
            return;
        }

        const paymentChannel = getPaymentChannel(state);

        // Don't proceed if not polling AND not PesaLink (manual check)
        if (!intervalId && paymentChannel !== 'Pesa link') {
            return;
        }

        // Don't check if already checking
        if (getPaystackStatusIsApiPending(state)) {
            return;
        }

        // Only set pending status for manual PesaLink checks, not for polling
        const isManualPesaLinkCheck = paymentChannel === 'Pesa link' && !intervalId;
        if (isManualPesaLinkCheck) {
            store.dispatch(apiTransitionPaystackStatus.pending());
        }

        try {
            const resp = await triggerStkStatusCheck(state);
            if (resp.data && resp.data.success) {
                const paymentStatus = resp.data.data.data.status;
                store.dispatch(checkPaystackStatusSuccess(resp.data));

                if (paymentStatus === 'success') {
                    // Payment successful - stop polling and submit
                    clearIntervalId(intervalId);
                    intervalId = null;
                    store.dispatch(apiTransitionPaystackStatus.success());
                    store.dispatch(submitPaystackPayments());
                } else if (paymentChannel === 'Pesa link') {
                    // PesaLink manual check - show appropriate modal based on status
                    if (paymentStatus === 'abandoned' || paymentStatus === 'ongoing') {
                        store.dispatch(apiTransitionPaystackStatus.success());
                    } else {
                        // Unknown PesaLink status - treat as failure
                        store.dispatch(apiTransitionPaystackStatus.failure());
                    }
                } else if (paymentChannel === 'Card' || paymentChannel === 'Airtel money') {
                    // Polling for Card/Airtel - if not success, reset to continue polling
                    store.dispatch(apiTransitionPaystackStatus.initialize());
                } else {
                    // Unknown status for other payment types - fail
                    clearIntervalId(intervalId);
                    intervalId = null;
                    store.dispatch(apiTransitionPaystackStatus.failure());
                }
                return;
            }
        } catch (e) {
            if (e?.response?.data?.status === 401 || e?.response?.data?.status === 403) {
                store.dispatch(setExpiredAuth(true));
            }
            // Clear polling on error to prevent repeated failures
            clearIntervalId(intervalId);
            intervalId = null;
            store.dispatch(apiTransitionPaystackStatus.failure());
            console.error(e.response);
            return e;
        }
    })();
    return result;
};

const paystackStatusPayload = (state) => {
    const paystackData = getPaystackData(state);
    const payload = {
        reference_code: paystackData.response.data.reference || null,
    };
    return {
        ...payload,
    };
};

const triggerStkStatusCheck = async (state) => {
    const loginData = getAccessToken(state);
    try {
        const url = '/om-api/digital-savings/paystack-status';
        return await axios.post(url, paystackStatusPayload(state), {
            headers: {
                'Content-Type': 'application/json',
                accessToken: loginData.accessToken,
                // Cookie: 'devEnvironment=ea-qa',
            },
        });
    } catch (e) {
        console.error(e);
        throw e;
    }
};
// ---------------------------- END -------------------

// ---------------------------- SUBMIT PAYMENT --------------------

export const submitPaymentService = (store, next, action) => {
    (async () => {
        try {
            const state = store.getState();
            const resp = await triggerSubmit(state);
            if (resp.data && resp.data.status === 200) {
                store.dispatch(confirmOrderSuccess(resp.data));

                const paymentChannel = getPaymentChannel(state);
                if (
                    paymentChannel === 'Pesa link' ||
                    paymentChannel === 'Airtel money' ||
                    paymentChannel === 'Bank transfer'
                ) {
                    store.dispatch(triggerConfirmOrder());
                }
            }
        } catch (e) {
            console.error(e);
        }
    })();

    return next(action);
};

const paystackSubmitPayload = (state) => {
    const paystackData = getPaystackData(state);
    const quoteId = getQuoteId(state);

    const payload = {
        quote_id: quoteId,
        reference_code: paystackData.response.data.reference || null,
    };
    return {
        ...payload,
    };
};

const triggerSubmit = async (state) => {
    const loginData = getAccessToken(state);
    try {
        const url = '/om-api/digital-savings/paystack-payment-submit';
        return await axios.post(url, paystackSubmitPayload(state), {
            headers: {
                'Content-Type': 'application/json',
                accessToken: loginData.accessToken,
                // Cookie: 'devEnvironment=ea-qa',
            },
        });
    } catch (e) {
        console.error(e);
        throw e;
    }
};
// ---------------------------- END -------------------
